// Übersicht des Eigentümerportals (Screens eigentuemer/01 und 06). Nur Widgets mit echten Daten
// (Entscheidung 22.10.2026): Ihr Objekt, Vermietung (wo wir vermieten), Zur Freigabe (wenn es Rechnungen gibt),
// Termine (nur freigegebene), Ansprechpartner. Auszahlungen und Versammlung fehlen, bis es Daten dazu gibt.
import { AddressBook, Buildings, CalendarBlank, CaretRight, CheckCircle, Door, EnvelopeSimple, FolderSimple, Key, SealCheck, Wrench } from '@phosphor-icons/react';
import { GlasWidget, StatusChip } from '../../components/portal/bausteine';
import { artIstWeg, euroGanz, objektAdresse, objektInfo, zahlDe, type EigDaten, type ObjektSicht } from '../../lib/portal/eigentuemer';
import { betrag, euro, rechnungUnterzeile, type EigTermin, type PortalRechnung } from '../../lib/portal/eigentuemerE7';
import { terminBlatt, terminZeit } from '../../lib/portal/mieterE5';
import { EINHEIT_STIL } from './EigentuemerEinheiten';
import { STUFE_TEXT, anfrageBeantwortet, anfrageStatusText, einsatzFilter, einsatzStufe, type EinsatzKurz } from '../../lib/portal/einsaetze';

const LINK_KNOPF = { alignSelf: 'flex-start', height: 34, padding: '0 4px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 5 } as const;

/** Ring: Vermietungsquote (Mietverwaltung) bzw. eigener MEA-Anteil (WEG). */
function Ring({ anteil, wert, label, tip }: { anteil: number; wert: string; label: string; tip: string }) {
  const r = 40;
  const umfang = 2 * Math.PI * r;
  return (
    <div data-tip={tip} style={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
      <svg width="96" height="96" viewBox="0 0 96 96" aria-hidden>
        <circle cx="48" cy="48" r={r} fill="none" stroke="var(--p-w08)" strokeWidth="9" />
        <circle cx="48" cy="48" r={r} fill="none" stroke="var(--p-schalter-an)" strokeWidth="9" strokeLinecap="round"
          strokeDasharray={`${umfang * Math.max(0, Math.min(1, anteil))} ${umfang}`} transform="rotate(-90 48 48)" />
      </svg>
      <span style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span className="p-zahl" style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1 }}>{wert}</span>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--p-w55)' }}>{label}</span>
      </span>
    </div>
  );
}

function fakten(s: ObjektSicht): [string, string][] {
  const f: [string, string][] = [];
  if (artIstWeg(s.art)) {
    if (s.einheiten.length) f.push([s.einheiten.length === 1 ? 'Ihre Einheit' : 'Ihre Einheiten', s.einheiten.map((e) => e.bezeichnung).join(', ')]);
    if (s.flaeche > 0) f.push(['Fläche', `${zahlDe(s.flaeche)} m²`]);
    if (s.hausgeld > 0) f.push(['Hausgeld / Monat', euroGanz(s.hausgeld)]);
    if (s.kaltmiete > 0) f.push(['Kaltmiete / Monat', euroGanz(s.kaltmiete)]);
    return f;
  }
  f.push(['Einheiten', String(s.einheiten.length)]);
  if (s.flaeche > 0) f.push(['Wohn- & Nutzfläche', `${zahlDe(s.flaeche, 0)} m²`]);
  if (s.mieterSichtbar && s.kaltmiete > 0) f.push(['Kaltmiete / Monat', euroGanz(s.kaltmiete)]);
  if (s.mieterSichtbar) f.push(['Leerstand', s.leer === 0 ? 'keiner' : `${s.leer} ${s.leer === 1 ? 'Einheit' : 'Einheiten'}`]);
  return f;
}

export default function EigentuemerUebersicht({ sicht, objektEig, rechnungen, termine, hatDokumente, einsaetze = [], onTab }: {
  sicht: ObjektSicht;
  objektEig: EigDaten['objekt_eig'];
  /** Rechnungen dieses Objekts (zur Freigabe + erledigt) */
  rechnungen: PortalRechnung[];
  /** Freigegebene Termine dieses Objekts */
  termine: EigTermin[];
  hatDokumente: boolean;
  /** #564: Einsätze dieses Objekts */
  einsaetze?: EinsatzKurz[];
  onTab: (t: 'einheiten' | 'rechnungen' | 'dokumente' | 'einsaetze') => void;
}) {
  const offen = rechnungen.filter((r) => r.stand === 'offen');
  const erste = offen[0] ?? null;
  const weg = artIstWeg(sicht.art);
  const ap = sicht.objekt.ansprechpartner?.name ? sicht.objekt.ansprechpartner : null;
  const apInitialen = (ap?.name ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
  const zeigeVermietung = !weg && sicht.mieterSichtbar && sicht.einheiten.length > 0;
  const ring = weg
    ? (sicht.mea > 0 ? { anteil: 1, wert: zahlDe(sicht.mea, 2), label: 'MEA', tip: 'Ihre Miteigentumsanteile' } : null)
    : (sicht.quote !== null ? { anteil: sicht.quote / 100, wert: `${sicht.quote} %`, label: 'vermietet', tip: 'Anteil der vermieteten Einheiten' } : null);
  const info = objektInfo(sicht, objektEig);

  return (
    <div className="ep-raster">
      {/* o: Ihr Objekt */}
      <GlasWidget farbe="wohnung" icon={Buildings} titel="Ihr Objekt" className="ep-o" style={{ padding: 18, gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0, flexWrap: 'wrap' }}>
          {ring && <Ring {...ring} />}
          <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--p-w85)', overflowWrap: 'anywhere' }}>{objektAdresse(sicht)}</span>
            {info && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--p-w50)' }}>{info}</span>}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          {fakten(sicht).map(([l, v]) => (
            <div key={l} style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '12px 14px', borderRadius: 16, background: 'var(--p-w05)', boxShadow: 'inset 0 0 0 0.5px var(--p-w06)', minWidth: 0 }}>
              <span className="p-kuerzen" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--p-w45)' }}>{l}</span>
              <span className="p-zahl p-kuerzen" style={{ fontSize: 19, fontWeight: 800 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="p-knopf" onClick={() => onTab('einheiten')} data-tip={weg ? 'Ihre Einheiten ansehen' : 'Alle Einheiten mit Mietern ansehen'} style={{ height: 40, border: 'none', background: 'var(--p-w08)', fontSize: 13 }}>
            <Door size={17} /> Einheiten
          </button>
          {hatDokumente && (
            <button type="button" className="p-knopf" onClick={() => onTab('dokumente')} data-tip="Unterlagen zum Objekt" style={{ height: 40, border: 'none', background: 'var(--p-w08)', fontSize: 13 }}>
              <FolderSimple size={17} /> Dokumente
            </button>
          )}
        </div>
      </GlasWidget>

      {/* v: Vermietung (nur wo wir vermieten) */}
      {zeigeVermietung && (
        <GlasWidget farbe="konto" icon={Key} titel="Vermietung"
          rechts={<span style={{ fontSize: 12, fontWeight: 800, color: 'var(--p-w50)' }}>{sicht.einheiten.length - sicht.leer} von {sicht.einheiten.length}</span>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sicht.einheiten.slice(0, 6).map((e) => {
              const stil = EINHEIT_STIL[e.status];
              return (
                <button key={e.id} type="button" className="p-zeile" onClick={() => onTab('einheiten')} data-tip={`${e.bezeichnung} ansehen`} style={{ gap: 10, padding: '9px 12px' }}>
                  <span style={{ minWidth: 52, maxWidth: '40%', flexShrink: 0, fontSize: 13, fontWeight: 800, textAlign: 'left' }} className="p-kuerzen">{e.bezeichnung}</span>
                  <span className="p-kuerzen" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--p-w60)', textAlign: 'left' }}>
                    {e.status === 'leer' ? (e.mieterSeit ? `Leerstand ${e.mieterSeit}` : 'Leerstand') : e.mieterName}
                  </span>
                  <StatusChip ton={stil.ton}>{stil.label}</StatusChip>
                </button>
              );
            })}
          </div>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => onTab('einheiten')} data-tip="Alle Einheiten mit Mietern ansehen" style={{ ...LINK_KNOPF, color: 'var(--p-blau-link)' }}>
            Alle Einheiten <CaretRight size={13} weight="bold" />
          </button>
        </GlasWidget>
      )}

      {/* f: Zur Freigabe (nur wenn es zu diesem Objekt Rechnungen für den Eigentümer gibt) */}
      {rechnungen.length > 0 && (
        <GlasWidget farbe={offen.length ? 'orange' : 'konto'} icon={SealCheck} titel="Zur Freigabe"
          rechts={offen.length ? <span className="p-zahl" style={{ fontSize: 12, fontWeight: 800, color: 'var(--p-ton-warn-fg)' }}>{offen.length} offen</span> : undefined}>
          {erste ? (
            <button type="button" className="p-zeile" onClick={() => onTab('rechnungen')} data-tip="Rechnung prüfen" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4, padding: '10px 12px' }}>
              <span className="p-kuerzen" style={{ fontSize: 14, fontWeight: 800, textAlign: 'left' }}>{(erste.bezeichnung ?? '').trim() || 'Rechnung'}</span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span className="p-kuerzen" style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--p-w50)', textAlign: 'left' }}>{rechnungUnterzeile(erste)}</span>
                <span className="p-zahl" style={{ fontSize: 15, fontWeight: 800 }}>{euro(betrag(erste))}</span>
              </span>
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px' }}>
              <CheckCircle size={22} weight="fill" style={{ color: 'var(--p-ton-ok-fg)' }} />
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 14, fontWeight: 800 }}>Alles erledigt</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--p-w50)' }}>Keine Rechnung wartet auf Sie</span>
              </span>
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => onTab('rechnungen')} data-tip={offen.length ? 'Rechnungen prüfen und freigeben' : 'Alle Rechnungen'} style={{ ...LINK_KNOPF, color: 'var(--p-blau-link)' }}>
            {offen.length ? 'Jetzt prüfen' : 'Alle Rechnungen'} <CaretRight size={13} weight="bold" />
          </button>
        </GlasWidget>
      )}

      {/* e: Einsätze (#564) – laufende zuerst */}
      {einsaetze.length > 0 && (() => {
        const laufend = einsaetze.filter((x) => einsatzFilter(x.status) !== 'erledigt');
        return (
          <GlasWidget farbe="orange" icon={Wrench} titel="Vorgänge"
            rechts={<span className="p-zahl" style={{ fontSize: 12, fontWeight: 800, color: 'var(--p-w50)' }}>{laufend.length} laufend</span>}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(laufend.length ? laufend : einsaetze).slice(0, 3).map((x) => (
                <button key={x.id} type="button" className="p-zeile" onClick={() => onTab('einsaetze')} data-tip="Alle Vorgänge ansehen" style={{ gap: 10, padding: '9px 12px' }}>
                  <span className="p-kuerzen" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, textAlign: 'left' }}>{x.titel}</span>
                  {x.art === 'anfrage'
                    ? <StatusChip ton={anfrageBeantwortet(x) ? 'ok' : 'neutral'}>{anfrageStatusText(x)}</StatusChip>
                    : <StatusChip ton={einsatzFilter(x.status) === 'erledigt' ? 'ok' : 'warn'}>{STUFE_TEXT[einsatzStufe(x.status)]}</StatusChip>}
                </button>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            <button type="button" onClick={() => onTab('einsaetze')} data-tip="Schäden, Einsätze und Anfragen mit Stand, Fotos und Kosten" style={{ ...LINK_KNOPF, color: 'var(--p-blau-link)' }}>
              Alle Vorgänge <CaretRight size={13} weight="bold" />
            </button>
          </GlasWidget>
        );
      })()}

      {/* t: Termine (nur vom Büro freigegebene) */}
      {termine.length > 0 && (
        <GlasWidget farbe="schluessel" icon={CalendarBlank} titel="Termine">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {termine.slice(0, 3).map((t) => {
              const b = terminBlatt(t.start_datum);
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', borderRadius: 14, background: 'var(--p-w05)', minWidth: 0 }}>
                  <span style={{ width: 46, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 0', borderRadius: 12, background: 'var(--p-w08)' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--c1)', textTransform: 'uppercase' }}>{b.monat}</span>
                    <span style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>{b.tag}</span>
                  </span>
                  <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span className="p-kuerzen" style={{ fontSize: 13.5, fontWeight: 800 }}>{t.titel}</span>
                    <span className="p-kuerzen" style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>{[terminZeit(t), t.firma].filter(Boolean).join(' · ')}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </GlasWidget>
      )}

      {/* a: Ansprechpartner (nur wenn das Objekt einen Zuständigen hat) */}
      {ap && (
        <GlasWidget farbe="kontakt" icon={AddressBook} titel="Ihre Ansprechpartner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 16, background: 'var(--p-w05)' }}>
            <span aria-hidden style={{ width: 40, height: 40, borderRadius: 13, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(160deg, var(--p-gold-text-hell), var(--p-gold-text))', color: 'var(--p-gold-ink)', fontSize: 13, fontWeight: 800 }}>{apInitialen}</span>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <span className="p-kuerzen" style={{ fontSize: 13.5, fontWeight: 800 }}>{ap.name}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>Ihr Verwalter</span>
            </span>
            {ap.email && (
              <a href={`mailto:${ap.email}`} data-tip={`E-Mail an ${ap.name}`} aria-label={`E-Mail an ${ap.name}`}
                style={{ width: 36, height: 36, borderRadius: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--p-mail-bg)', color: 'var(--p-mail-ink)' }}>
                <EnvelopeSimple size={16} weight="fill" />
              </a>
            )}
          </div>
        </GlasWidget>
      )}
    </div>
  );
}
